import { useEffect, useMemo, useState } from "react";
import { Gift, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";

export function FreeGiftToggle() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [threshold, setThreshold] = useState("100");
  const [productId, setProductId] = useState("");
  const [variationId, setVariationId] = useState("");
  const [products, setProducts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/promo/free-gift"),
      api.get("/products"),
    ]).then(([cfg, cat]) => {
      const c = cfg.data || {};
      setEnabled(!!c.enabled);
      setThreshold(String(c.threshold ?? 100));
      setProductId(c.product_id || "");
      setVariationId(c.variation_id || "");
      setProducts(cat.data || []);
    }).catch(() => toast.error("Couldn't load the promotion settings."))
      .finally(() => setLoading(false));
  }, []);

  const variations = useMemo(() => {
    const p = products.find((x) => String(x.id) === String(productId));
    return (p?.strengths || []).filter((s) => s.variation_id);
  }, [products, productId]);

  const onProductChange = (id) => {
    setProductId(id);
    const p = products.find((x) => String(x.id) === String(id));
    const vars = (p?.strengths || []).filter((s) => s.variation_id);
    setVariationId(vars.length ? vars[0].variation_id : "");
  };

  const save = async () => {
    const t = Number(threshold);
    if (!productId) { toast.error("Pick a gift product."); return; }
    if (!Number.isFinite(t) || t < 0) { toast.error("Enter a valid threshold amount."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/promo/free-gift", {
        enabled, threshold: t, product_id: productId, variation_id: variationId || null,
      });
      setEnabled(!!data.enabled);
      setThreshold(String(data.threshold));
      setProductId(data.product_id || "");
      setVariationId(data.variation_id || "");
      toast.success("Free gift promotion saved");
    } catch {
      toast.error("Couldn't save the promotion.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-soft rounded-2xl p-6 mb-8" data-testid="free-gift-toggle-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#f4e7c4] text-[#8a681c] flex items-center justify-center shrink-0"><Gift className="w-5 h-5" /></div>
          <div>
            <h3 className="font-display font-bold text-lg">Free Gift Promotion</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Automatically add a complimentary item (shown as FREE) to any cart whose after-discount subtotal reaches your threshold. One per order.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEnabled((v) => !v)}
          disabled={loading || saving}
          data-testid="free-gift-toggle"
          aria-pressed={enabled}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 disabled:opacity-60 ${enabled ? "bg-[#c59b27]" : "bg-[#d8cba6]"}`}
        >
          <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground mt-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading settings…</p>
      ) : (
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Unlock at (after discount)</label>
            <div className="mt-2 flex items-center rounded-md border border-input bg-background h-10 px-3">
              <span className="text-sm text-muted-foreground mr-1">$</span>
              <input type="number" min="0" step="1" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="free-gift-threshold" className="w-full bg-transparent text-sm outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Gift product</label>
            <select value={productId} onChange={(e) => onProductChange(e.target.value)} data-testid="free-gift-product" className="mt-2 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
              <option value="" disabled>Select a product…</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Strength</label>
            <select value={variationId} onChange={(e) => setVariationId(e.target.value)} disabled={!variations.length} data-testid="free-gift-variation" className="mt-2 w-full h-10 rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60">
              {variations.length ? (
                variations.map((v) => <option key={v.variation_id} value={v.variation_id}>{v.label}</option>)
              ) : (
                <option value="">No strengths (single option)</option>
              )}
            </select>
          </div>
        </div>
      )}

      {!loading && (
        <div className="mt-5 flex items-center justify-between gap-3">
          <p className="text-xs font-semibold flex items-center gap-1" data-testid="free-gift-status">
            {enabled ? "Promotion is ON" : "Promotion is OFF"}
          </p>
          <button type="button" onClick={save} disabled={saving} data-testid="free-gift-save" className="pill px-4 h-10 btn-ink text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}
